import React from "react";
import { Box, Skeleton, Typography } from "@mui/material";

// Shared by the card and its skeleton so the ghost card has the exact same
// footprint — the same reason the desktop skeletons reuse `classes.cardContainer`.
const CARD_SX = {
  border: "1px solid",
  borderColor: "neutral.clr-100",
  borderRadius: 3,
  p: 3,
  display: "flex",
  flexDirection: "column",
  gap: 2,
};

const SCORE_BOX_SX = {
  flex: 1,
  border: "1px solid",
  borderColor: "neutral.clr-100",
  borderRadius: 2,
  py: 2,
  textAlign: "center",
};

/** Loading placeholder for <AttemptCard/>, mirroring its title/meta/scores. */
export function AttemptCardSkeleton() {
  return (
    <Box sx={CARD_SX}>
      {/* Title + type */}
      <Box
        sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}
      >
        <Skeleton variant="rectangular" width="55%" height={20} />
        <Skeleton variant="rectangular" width={64} height={14} />
      </Box>

      {/* Time + attempt date */}
      <Box sx={{ display: "flex", gap: 4 }}>
        <Skeleton variant="rectangular" width={88} height={14} />
        <Skeleton variant="rectangular" width={112} height={14} />
      </Box>

      {/* Score / Speech / Content tiles */}
      <Box sx={{ display: "flex", gap: 2, mt: 0.5 }}>
        {Array.from(new Array(3)).map((_, i) => (
          <Box
            key={i}
            sx={{
              ...SCORE_BOX_SX,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 0.75,
            }}
          >
            <Skeleton variant="rectangular" width="55%" height={12} />
            <Skeleton variant="rectangular" width="35%" height={18} />
          </Box>
        ))}
      </Box>
    </Box>
  );
}

function ScoreBox({ label, value }) {
  return (
    <Box sx={SCORE_BOX_SX}>
      <Typography
        variant="body02-regular"
        color="neutral.clr-500"
        sx={{ display: "block", mb: 0.5 }}
      >
        {label}
      </Typography>
      <Typography variant="h6-bold" color="neutral.clr-900">
        {value}
      </Typography>
    </Box>
  );
}

/**
 * A single attempted-assessment card (mobile). Tapping it opens the attempt's
 * feedback — same target as the desktop AttemptsTable row click
 * (`/performance/:id`).
 */
export default function AttemptCard({ attempt, onClick }) {
  const { title, typeLabel, timeLabel, date, score, speech, content } = attempt;

  return (
    <Box onClick={onClick} sx={{ ...CARD_SX, cursor: "pointer" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Typography
          variant="body01-semiBold"
          color="neutral.clr-800"
          sx={{ textTransform: "capitalize" }}
        >
          {title}
        </Typography>
        <Typography
          variant="body02-regular"
          color="neutral.clr-400"
          sx={{ textTransform: "capitalize", whiteSpace: "nowrap" }}
        >
          {typeLabel}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", gap: 4 }}>
        <Typography variant="body02-regular" color="neutral.clr-600">
          Time: {timeLabel}
        </Typography>
        <Typography variant="body02-regular" color="neutral.clr-600">
          Attempt: {date}
        </Typography>
      </Box>

      <Box sx={{ display: "flex", gap: 2, mt: 0.5 }}>
        <ScoreBox label="Score" value={score} />
        <ScoreBox label="Speech" value={speech} />
        <ScoreBox label="Content" value={content} />
      </Box>
    </Box>
  );
}
